import { getDashboardChartData } from "helpers";
import { getWeeksInMonth } from "helpers/dateTime";
import "react-responsive-pagination/themes/classic.css";
import moment from "moment";
import toast from "react-hot-toast";
import React, { useEffect, useRef, useState } from "react";
import { flattenDeep, truncate } from "lodash";

import Button from "components/common/Button";
import CustomCalendar from "components/common/CalendarComp.js/Calendar";
import CustomToast from "components/common/CustomToast";
import Dropdown from "components/common/Dropdown";
import useGetDashboardGWM from "hooks/Dashboard/useGetDashboardGWM";
import useGetBundleDashboardGWM from "hooks/Dashboard/useGetBundleDashboardGWM";
import useGetProductList from "hooks/inventoryHooks/useGetProductList";
import { useAppDispatch, useAppSelector } from "hooks/storeHooks";
import { dashboardActions } from "redux/Ldb/actions";
import { AccountPermissions } from "types/permissions";
import { getSalesChannelIcon } from "utils/get-sales-channel-icon";
import { ForecastMethodEnum } from "./enums/ForecastMethodEnum";
import { FORECAST_SKU_TYPE } from "./constant/forecastConstant";
import TextInput from "components/common/InputField/TextInput";
import ForecastConfigurationSingleTable from "./ForecastConfigurationSingleTable";
import ForecastConfigurationBundleTable from "./ForecastConfigurationBundleTable";
import { getSalesChannelIconRaw } from "utils/get-sales-channel-icon-raw";
import { ForecastConfigChangeValues } from "./types/forcastConfig";

type ForecastConfigurationProps = {
    activeForecastViewType?: number;
    salesChannel?: { [key: string]: any }[] | string[];
    onCloseConfig?: () => void;
    skuType: number;
};

const ForecastConfiguration = ({
    activeForecastViewType,
    salesChannel,
    onCloseConfig,
    skuType,
}: ForecastConfigurationProps) => {
    const [activePeriod, setActivePeriod] = useState<number>(0);
    const [durationFilter, setDurationFilter] = useState<string>("This year");
    const [customFilter, setCustomFilter] = useState<string>("");
    const [startAndEndDate, setStartAndEndDate] = useState<string[]>([]);
    const [chartFilter, setChartFilter] = useState<string>("yearly");
    const [customValue, customOnChange] = useState("");
    const [showCustomDate, setShowCustomDate] = useState<boolean>(false);
    const [categoryFilter, setCategoryFilter] = useState<string>(
        skuType === FORECAST_SKU_TYPE.SINGLE ? "Finished Goods" : ""
    );
    const [productFilter, setProductFilter] = useState<string[]>([]);
    const [channelFilter, setChannelFilter] = useState<string>("Shopify");
    const [monthlyConfigChange, setMonthlyConfigChange] = useState<{ [key: string]: any }[]>([]);
    const [weeklyConfigChange, setWeeklyConfigChange] = useState<{ [key: string]: any }[]>([]);
    const [chartData, setChartData] = useState<{ [key: string]: any }>([]);
    const [refresh, setRefresh] = useState<boolean>(false);

    const clearSearchRef = useRef(null);
    const customRef = useRef(null);

    const currentDate = moment();
    const monthlyTab = chartData?.[0]?.filter((month) => {
        const monthDate = moment(month, "MMM YYYY"); // Parse the month using moment.js
        return monthDate.isSameOrAfter(currentDate, "month"); // Check if the month is the current month or a future month
    });

    const dateValues = [
        {
            period: "This year",
            value: "yearly",
        },
        {
            period: "This month",
            value: "monthly",
        },
        {
            period: "custom",
            value: "custom",
        },
    ];

    const dispatch = useAppDispatch();
    const { addForecastConfigSuccess, addingForecastConfig } = useAppSelector(
        (state) => state.dashboard
    );

    const singleResult = useGetDashboardGWM(true, refresh, setRefresh);
    const bundleResult = useGetBundleDashboardGWM(true, refresh, setRefresh);

    const {
        data: gwmData,
        handleDateRangeChange,
        handleChannelChange,
        handleCategoryChange,
        handleProductChange,
        productIds,
        handleStartAndEndDateChange,
    } = skuType === FORECAST_SKU_TYPE.SINGLE ? singleResult : bundleResult;

    const {
        productSearchList,
        handleDebouncedChangeTwo,
        handleSearchTwo,
        searchTwo,

        isLoadingMore,
    } = useGetProductList(null, "", false, false, "", false);

    const salesChannelOption = (salesChannel as string[])?.map((channel) => {
        return {
            label: channel,
            value: channel,
            icon: getSalesChannelIcon(channel),
        };
    });

    const handleDateChange = (e) => {
        const { value } = e.target;
        const [val, period] = value.split("-");
        setDurationFilter(period);
        setCustomFilter(val);

        if (val !== "custom") {
            handleDateRangeChange(val);
            setStartAndEndDate([]);
            setChartFilter(val);
            customOnChange("");
        } else {
            setShowCustomDate(true);
        }
    };

    const handleSaveConfig = () => {
        const parsedData = [
            ...(chartFilter === "yearly" ? monthlyConfigChange : weeklyConfigChange),
        ]?.map((item) => {
            return Object.values(item)[0];
        });
        const body = {
            configs: flattenDeep(parsedData),
        };

        dispatch(dashboardActions.addForecastConfig(body));
    };

    const handleProductValue = (e) => {
        const { value } = e.target;

        const prodIdsCopy = [...productIds];
        const productFilterCopy = [...productFilter];
        const newValue = value?.split("-")[0];
        const newName = value?.split("-")[1];
        const isValuePresent = prodIdsCopy?.find((id) => id === newValue);

        if (isValuePresent) {
            handleProductChange(prodIdsCopy?.filter((id) => id !== newValue));
            setProductFilter(productFilterCopy?.filter((name) => name !== newName));
        } else {
            if (newValue === "") {
                handleProductChange([]);
                setProductFilter([]);
            } else {
                setProductFilter((prev) => [...prev, newName]);
                handleProductChange([...prodIdsCopy, newValue]);
            }
        }

        handleCategoryChange("");
        setCategoryFilter("");
    };

    const handleChangeValue = (e) => {
        const { value } = e.target;

        setChannelFilter(value);

        if (value === "All channels") {
            handleChannelChange("");
        } else {
            handleChannelChange(value);
        }
    };

    const handleConfigChange = (values: ForecastConfigChangeValues, inventory, weekData) => {
        const { adjustedDemandForecast: value, forecastMethod } = values;
        const date = moment(monthlyTab?.[activePeriod], "MMMM YYYY");
        const year = date.year();
        const month = date.month() + 1;

        const today = moment();
        const thisMonth = today.month();
        const thisYear = today.year();
        const currMonth = today?.format("MMM-YYYY");
        const nOfWeeksOfTheMonth = getWeeksInMonth(thisYear, thisMonth);

        const keyFormat = chartFilter === "yearly" ? monthlyTab?.[activePeriod] : currMonth;
        const configCopy = [
            ...(chartFilter === "yearly" ? monthlyConfigChange : weeklyConfigChange),
        ];
        const conf = configCopy.find((item) => item[keyFormat]?.inventoryId === inventory?._id);
        const configIndex = configCopy.findIndex(
            (item) => item[keyFormat]?.inventoryId === inventory?._id
        );
        const weekValueFromMonthValue =
            Number(String(value)?.split(",")?.join("") || 0) / nOfWeeksOfTheMonth;

        const yearlyData = {
            year: `${year}-01-01`,
            forecastLevel: "global",
            channel: channelFilter,
            forecastMethod: forecastMethod || ForecastMethodEnum.synth,
            monthlyDemand: {
                month: month,
                quantity: Number(String(value)?.split(",")?.join("") || 0),
            },
            weeklyDemand: Array(nOfWeeksOfTheMonth)
                .fill(0)
                ?.map((val, idx) => {
                    return {
                        week: idx + 1,
                        quantity: weekValueFromMonthValue,
                    };
                }),
            inventoryId: inventory?._id,
        };

        const weeklyValues = conf?.[keyFormat]?.weeklyDemand?.map((item) => item?.quantity);

        const weeklyData = [...(weeklyValues || [])];
        weeklyData[activePeriod] = Number(String(value)?.split(",")?.join(""));

        const monthlyData = {
            year: `${thisYear}-01-01`,
            forecastLevel: "global",
            channel: channelFilter,
            forecastMethod: forecastMethod || ForecastMethodEnum.synth,
            weeklyDemand: [...(weekData?.length > weeklyData?.length ? weekData : weeklyData)]?.map(
                (val, idx) => {
                    return {
                        week: idx + 1,
                        quantity:
                            Number(weeklyData?.[idx]) >= 0
                                ? Number(weeklyData?.[idx])
                                : Number(
                                      String(weekData?.[idx]?.weeklyDemand?.quantity)
                                          ?.split(",")
                                          ?.join("")
                                  ) || 0,
                    };
                }
            ),
            monthlyDemand: {
                month: thisMonth + 1,
                quantity: weeklyData?.reduce((acc, curr) => acc + Number(curr), 0),
            },
            inventoryId: inventory?._id,
        };

        if (configIndex === -1) {
            configCopy.push({ [keyFormat]: chartFilter === "yearly" ? yearlyData : monthlyData });
        } else {
            configCopy[configIndex] = {
                [keyFormat]: chartFilter === "yearly" ? yearlyData : monthlyData,
            };
        }

        if (chartFilter === "yearly") {
            setMonthlyConfigChange(configCopy);
        } else {
            setWeeklyConfigChange(configCopy);
        }
    };

    const forecastMethodOptions = [
        {
            label: "Use a fixed value until historical data becomes available.",
            description:
                "Applies your forecast temporarily. Once actual sales data becomes available, Synth will start generating forecasts based on those available sales data.",
            value: ForecastMethodEnum.manual,
        },
        {
            label: "Use Synth’s forecast based on historical accuracy",
            description:
                "Applies Synth's predicted forecast using past accuracy trends to determine the most reliable forecast for this product.",
            value: ForecastMethodEnum.synth,
        },
    ];

    useEffect(() => {
        if (gwmData) {
            let chartDataValues;
            if (customFilter === "custom" && startAndEndDate?.length === 2) {
                chartDataValues = getDashboardChartData(gwmData, "custom", startAndEndDate);
            } else {
                chartDataValues = getDashboardChartData(gwmData, chartFilter);
            }
            setChartData(chartDataValues);
        }
    }, [chartFilter, gwmData, startAndEndDate]);

    useEffect(() => {
        if (Boolean(addForecastConfigSuccess)) {
            toast.custom((t) => (
                <CustomToast t={t} message={"Configurations saved successfully"} type={"success"} />
            ));
            dispatch(dashboardActions.resetAddForecastConfig());
        }
    }, [dispatch, addForecastConfigSuccess]);

    useEffect(() => {
        if (durationFilter === "custom" && customValue[0] !== customValue[1]) {
            const dates = [
                moment(customValue?.[0]).format("MMM DD YYYY"),
                moment(customValue?.[1]).format("MMM DD YYYY"),
            ];

            setStartAndEndDate([dates?.[0], dates?.[1]]);
            if (customValue?.[0] !== customValue?.[1]) {
                handleStartAndEndDateChange(customValue);
            }
        }
    }, [durationFilter, customValue]);

    const activeChannelIcon =
        typeof getSalesChannelIconRaw(channelFilter) === "string"
            ? getSalesChannelIconRaw(channelFilter)
            : getSalesChannelIcon(channelFilter);

    const contentProps = {
        categoryFilter,
        channelFilter,
        productIds,
        chartFilter,
        handleConfigChange,
        monthlyConfigChange,
        weeklyConfigChange,
        chartData,
        forecastMethodOptions,
        monthlyTab,
        activePeriod,
    };

    return (
        <div>
            <div className="absolute top-[15px] right-[32px]">
                <div className="flex sm:space-x-2.5  max-sm:justify-between max-sm: items-end border-b border-b-slate-200 sm:border-b-0">
                    <Dropdown
                        handleChange={(name, value) => {
                            handleProductValue({
                                target: { name, value },
                            });
                        }}
                        dropdown={
                            <div className="flex items-center sm:rounded-[6px] sm:shadow-buttonShadow2 pt-[6px] sm:py-[6px] xs:px-[10px] xs:gap-2 sm:!border sm:!border-slate-100">
                                <div
                                    className={`text-g-75 font-rocGroteskMedium text-[11px] xs:text-[13px] inline-block`}
                                >
                                    {productFilter?.length > 0
                                        ? truncate(productFilter?.join(","), {
                                              length: 15,
                                          })
                                        : "All product"}
                                </div>
                                <i className="ri-arrow-down-s-line text-xs xs:text-lg text-g-75"></i>
                            </div>
                        }
                        dropdownItemsClasses="!px-0 !py-0"
                        dropdownClassName="!w-full !mb-10 !-mt-7"
                        dropdownContainerClasses={`sm:shadow-cardShadow-4 border border-slate-100 right-0 rounded-[6px] 
                                            !min-h-[300px] !max-h-[300px] !w-auto !pt-0
                                    `}
                        loader={isLoadingMore}
                        name="valueRange"
                        search={
                            <div ref={clearSearchRef} className="">
                                <TextInput
                                    value={searchTwo}
                                    name={"search"}
                                    type={"text"}
                                    required={false}
                                    onChange={handleSearchTwo}
                                    onInput={handleDebouncedChangeTwo}
                                    inputPlaceholder="Search product"
                                    containerClassname=""
                                    inputContainerClassname={"!h-[40px]"}
                                    // inputClassName="w-[50%]"
                                />
                            </div>
                        }
                        leaveOpen={true}
                        dropdownOptions={[
                            {
                                label: (
                                    <div
                                        className=" text-slate-600 font-rocGroteskMedium text-[13px] flex items-center gap-2
                                                        border-b border-b-slate-100 !w-[190px] px-2 py-[10px]"
                                    >
                                        <i
                                            className={`${
                                                productIds?.length === 0
                                                    ? "ri-checkbox-line text-carribean-green"
                                                    : "ri-checkbox-blank-line text-slate-200"
                                            }    text-xl`}
                                        ></i>
                                        All product
                                    </div>
                                ),
                                value: "",
                            },
                            ...productSearchList?.map((item, idx) => ({
                                label: (
                                    <div
                                        key={idx}
                                        className=" text-slate-600 font-rocGroteskMedium text-[13px] gap-2 flex items-center
                                                        border-b border-b-slate-100 !w-[190px] px-2 py-[10px]"
                                    >
                                        <i
                                            className={`${
                                                productIds?.includes(item?._id)
                                                    ? "ri-checkbox-line text-carribean-green"
                                                    : "ri-checkbox-blank-line text-slate-200"
                                            }   text-xl`}
                                        ></i>

                                        <div className="flex flex-col">
                                            <p className="text-xs mb-1">{` SKU: ${item?.sku}`}</p>
                                            <span className="text-[10px] leading-normal">
                                                {truncate(item?.productName, {
                                                    length: 50,
                                                })}
                                            </span>
                                        </div>
                                    </div>
                                ),
                                value: item?._id + "-" + item?.productName,
                            })),
                        ]}
                    />

                    <Dropdown
                        handleChange={(name, value) => {
                            handleChangeValue({
                                target: { name, value },
                            });
                        }}
                        dropdown={
                            <div className="flex items-center justify-between rounded-[6px] sm:shadow-buttonShadow2 pt-[6px] sm:py-[6px] xs:px-[10px] xs:gap-2 border-0 sm:!border sm:!border-slate-100">
                                <div className="rounded-full shadow-sm p-[1px] border border-[#E2E8F0] flex justify-center items-center w-[22px] h-[22px]">
                                    {typeof activeChannelIcon === "string" ? (
                                        <img
                                            src={activeChannelIcon as string}
                                            alt="channel"
                                            className="w-full max-w-[20px]"
                                        />
                                    ) : (
                                        activeChannelIcon
                                    )}
                                </div>
                                <div
                                    className={`text-g-75 font-rocGroteskMedium text-[11px] xs:text-[13px] inline-block  ${
                                        channelFilter && "capitalize"
                                    }`}
                                >
                                    {truncate(channelFilter, {
                                        length: 15,
                                    }) || "All channels"}
                                </div>
                                <i className="ri-arrow-down-s-line text-xs xs:text-lg text-g-75"></i>
                            </div>
                        }
                        dropdownItemsClasses=""
                        dropdownClassName="!mb-10 !-mt-7"
                        dropdownContainerClasses={`shadow-cardShadow-4 border border-slate-100 right-0 rounded-[6px] !max-h-[200px] !w-[190px] !min-w-min
                                    !py-0`}
                        name="valueRange"
                        dropdownOptions={[
                            ...salesChannelOption?.map((item, idx) => ({
                                label: item.label,
                                value: item.value,
                                icon: item?.icon || null,
                            })),
                        ]}
                    />

                    <div className="">
                        <Dropdown
                            handleChange={(name, value) => {
                                setActivePeriod(0);
                                handleDateChange({ target: { name, value } });
                            }}
                            dropdown={
                                <div className="flex items-center justify-between rounded-[6px] shadow-buttonShadow2 py-[6px] px-[10px] gap-2 !border !border-slate-100 max-sm:hidden">
                                    <i className="ri-arrow-down-s-line text-lg text-[#142837]"></i>

                                    <div
                                        className={`text-g-75 font-rocGroteskMedium text-[13px] inline-block ${
                                            durationFilter === "custom" && "capitalize"
                                        } `}
                                    >
                                        {startAndEndDate?.length !== 0
                                            ? ` ${startAndEndDate[0]} to ${startAndEndDate[1]}`
                                            : durationFilter || "This year"}
                                    </div>
                                </div>
                            }
                            dropdownItemsClasses="!px-0 !py-0"
                            dropdownClassName="!w-full !mb-10 !-mt-7"
                            dropdownContainerClasses={`shadow-cardShadow-4 border border-slate-100 right-0 rounded-[6px] !max-h-fit !w-auto`}
                            name="valueRange"
                            dropdownOptions={[
                                ...dateValues
                                    ?.filter((item) =>
                                        activeForecastViewType === 0
                                            ? true
                                            : item?.value !== "custom"
                                    )
                                    ?.map((item, idx) => ({
                                        label: (
                                            <div
                                                key={idx}
                                                className={`${
                                                    item.period === "custom" && "capitalize"
                                                } text-g-75 font-rocGroteskMedium text-[13px] flex justify-between
                                                        border-b border-b-slate-100 !w-[120px] px-4 py-[10px]`}
                                            >
                                                {item?.period}
                                            </div>
                                        ),
                                        value: item?.value + "-" + item?.period,
                                    })),
                            ]}
                        />
                    </div>

                    <div>
                        {showCustomDate && (
                            <div
                                ref={customRef}
                                className="w-[630px]  z-[10000] absolute  right-0 top-16 px-[28px] pt-8 pb-8 bg-white
                                                    rounded-2xl shadow-[0px_0px_4px_rgba(0,0,0,0.04),0px_4px_32px_rgba(0,0,0,0.06)]"
                            >
                                <CustomCalendar
                                    showDoubleView={true}
                                    selectRange={true}
                                    value={customValue}
                                    onChange={customOnChange}
                                />
                                <div className="flex justify-end">
                                    <Button
                                        btnText={"Close"}
                                        btnClassname={"!w-fit !py-2"}
                                        onClick={() => setShowCustomDate(false)}
                                    />
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="flex items-center space-x-2.5">
                        <Button
                            btnText="Close"
                            btnClassname="!bg-white !text-[#E4281D] !border !border-slate-100 shadow-[0px_1px_2px_0px_#5B687126] !text-[13px] !px-2 !py-2.5 rounded-[5px] !h-[42px]"
                            icon={<i className="ri-close-fill text-base"></i>}
                            onClick={() => {
                                onCloseConfig();
                                setMonthlyConfigChange([]);
                                setWeeklyConfigChange([]);
                            }}
                        />
                        <Button
                            btnText={`Apply configuration for ${channelFilter}`}
                            btnClassname="!bg-[#0AAD0A] !text-white !text-[13px] !whitespace-nowrap !px-[12px] !py-2.5 rounded-[5px] !h-[42px] font-rocGroteskRegular"
                            icon={<i className="ri-check-double-line text-base"></i>}
                            btnPermission={[
                                AccountPermissions.all_access,
                                AccountPermissions.view_dashboard,
                            ]}
                            isLoading={addingForecastConfig}
                            onClick={() => handleSaveConfig()}
                        />
                    </div>
                </div>
            </div>

            <div className="flex items-center justify-between pt-4 border-t">
                {monthlyTab && chartData?.[0] && (
                    <div className="flex items-center space-x-2.5">
                        {[...(chartFilter === "yearly" ? monthlyTab : chartData?.[0])]?.map(
                            (item, idx) => {
                                return (
                                    <span
                                        onClick={() => setActivePeriod(idx)}
                                        className={`text-sm font-rocGroteskMedium text-tradeally-neutral-200 cursor-pointer pb-2.5 ${
                                            activePeriod === idx &&
                                            "text-gm-50 border-b-2 border-[#FFB903]"
                                        }`}
                                    >
                                        {item}
                                    </span>
                                );
                            }
                        )}
                    </div>
                )}

                <div className="flex items-center space-x-1"></div>
            </div>

            <div>
                {skuType === FORECAST_SKU_TYPE.SINGLE ? (
                    <ForecastConfigurationSingleTable {...contentProps} />
                ) : (
                    <ForecastConfigurationBundleTable {...contentProps} />
                )}
            </div>
        </div>
    );
};

export default ForecastConfiguration;
